import _ from 'lodash';
import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  Paper,
  Stack,
} from '@mui/material';
import RouteFields from './RouteFields';
import BodyParametersSection from './BodyParametersSection';
import arrayMove from '@/util/arrayMove';

const path_re = /^\/[a-zA-z0-9_/\-.{}]*/;
const file_re = /^[a-zA-z0-9][a-zA-z0-9_/\-.]*[a-zA-z0-9_\-.]$/;
const python_re = /^[A-Za-z_][A-Za-z_0-9]*$/;

export default function EndpointRoute({
  index,
  initialValues,
  duplicate,
  onChange,
  edit,
  removeRoute,
}) {
  const [values, setValues] = useState(
    initialValues || {
      path: '',
      verb: 'POST',
      file: '',
      function: '',
      body: [],
      positional: true,
    }
  );

  const [errors, setErrors] = useState({
    path_error: false,
    file_error: false,
    function_error: false,
    body_errors: initialValues?.body && Array.isArray(initialValues.body)
      ? new Array(initialValues.body.length).fill({
          name_error: false,
          dup_error: false,
          default_value_error: false,
        })
      : [],
  });

  const isInitialMount = useRef(true);
  const prevValuesRef = useRef(values);
  const prevErrorsRef = useRef(errors);

  const bodyErrorCheck = (body) => {
    const params = body.map((param) => {
      if (param.name) {
        return param.name;
      }
      return undefined;
    });
    const dup_param = params.filter((e, i, a) => a.indexOf(e) !== i);
    const errors = body.map((param) => {
      const error = {
        name_error: false,
        dup_error: false,
        default_value_error: false,
      };
      if (param.name) {
        error.dup_error = dup_param.includes(param.name);
        error.name_error = !python_re.test(param.name);
      }
      if (param.optional) {
        error.default_value_error = param.default_value === '';
      }
      return error;
    });
    return errors;
  };

  // Call onChange when values or errors change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevValuesRef.current = values;
      prevErrorsRef.current = errors;
      const body_error = new Set(
        errors.body_errors.map((error) => Object.values(error)).flat()
      );
      onChange(
        index,
        values,
        errors.path_error ||
          errors.file_error ||
          errors.function_error ||
          body_error.has(true)
      );
      return;
    }

    // Only call onChange if values or errors actually changed
    const valuesChanged = !_.isEqual(prevValuesRef.current, values);
    const errorsChanged = !_.isEqual(prevErrorsRef.current, errors);

    if (valuesChanged || errorsChanged) {
      prevValuesRef.current = values;
      prevErrorsRef.current = errors;
      const body_error = new Set(
        errors.body_errors.map((error) => Object.values(error)).flat()
      );

      onChange(
        index,
        values,
        errors.path_error ||
          errors.file_error ||
          errors.function_error ||
          body_error.has(true)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, errors, index]);

  const handleFieldChange = (name, value) => {
    switch (name) {
      case 'path':
        setErrors((prev) => ({
          ...prev,
          path_error: !path_re.test(value) || value === '',
        }));
        setValues((prev) => ({ ...prev, [name]: value }));
        break;
      case 'file':
        setErrors((prev) => ({
          ...prev,
          file_error: !file_re.test(value) || value === '',
        }));
        setValues((prev) => ({ ...prev, [name]: value }));
        break;
      case 'function':
        setErrors((prev) => ({
          ...prev,
          function_error: !python_re.test(value) || value === '',
        }));
        setValues((prev) => ({ ...prev, [name]: value }));
        break;
      case 'positional':
        setValues((prev) => ({
          ...prev,
          positional: !prev.positional,
        }));
        break;
      default:
        setValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleBodyParameterChange = (paramIndex, paramValue) => {
    const body = [...values.body];
    body[paramIndex] = paramValue;
    const body_errors = bodyErrorCheck(body);
    setErrors((prev) => ({
      ...prev,
      body_errors,
    }));
    setValues((prev) => ({
      ...prev,
      body,
    }));
  };

  const handleAddBodyParameter = () => {
    const newBody = [
      ...values.body,
      {
        name: '',
        type: '',
        optional: false,
        default_value: '',
      },
    ];
    const newBodyErrors = bodyErrorCheck(newBody);
    setErrors((prev) => ({
      ...prev,
      body_errors: newBodyErrors,
    }));
    setValues((prev) => ({
      ...prev,
      body: newBody,
    }));
  };

  const handleRemoveBodyParameter = (paramIndex) => {
    const body = [...values.body];
    body.splice(paramIndex, 1);
    const newBodyErrors = bodyErrorCheck(body);
    setErrors((prev) => ({
      ...prev,
      body_errors: newBodyErrors,
    }));
    setValues((prev) => ({
      ...prev,
      body,
    }));
  };

  const handleMoveBodyParameterUp = (paramIndex) => {
    if (paramIndex === 0) {
      return;
    }
    const body = arrayMove(values.body, paramIndex, paramIndex - 1);
    const body_errors = bodyErrorCheck(body);
    setErrors((prev) => ({
      ...prev,
      body_errors,
    }));
    setValues((prev) => ({
      ...prev,
      body,
    }));
  };

  const handleMoveBodyParameterDown = (paramIndex) => {
    if (paramIndex === values.body.length - 1) {
      return;
    }
    const body = arrayMove(values.body, paramIndex, paramIndex + 1);
    const body_errors = bodyErrorCheck(body);
    setErrors((prev) => ({
      ...prev,
      body_errors,
    }));
    setValues((prev) => ({
      ...prev,
      body,
    }));
  };

  return (
    <Grid size={12}>
      <Paper elevation={1} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <RouteFields
            values={values}
            errors={errors}
            duplicate={duplicate}
            onChange={handleFieldChange}
            onRemove={() => removeRoute(index)}
            edit={edit}
          />

          {['POST', 'PUT', 'PATCH'].includes(values.verb) && (
            <BodyParametersSection
              body={values.body}
              body_errors={errors.body_errors}
              positional={values.positional}
              onParameterChange={handleBodyParameterChange}
              onAddParameter={handleAddBodyParameter}
              onRemoveParameter={handleRemoveBodyParameter}
              onMoveUp={handleMoveBodyParameterUp}
              onMoveDown={handleMoveBodyParameterDown}
              onPositionalChange={(checked) =>
                handleFieldChange('positional', checked)
              }
              edit={edit}
            />
          )}
        </Stack>
      </Paper>
    </Grid>
  );
}

